import { useCallback, useEffect, useState } from "react";
import { CalendarIcon, Filter, Loader2, RefreshCw, X } from "lucide-react";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Separator } from "@/components/ui/separator";
import { getDistributionProcessesAll } from "@/Api/distributions";
import { searchContestsData } from "@/Api/contests";
import { searchUnitiesData } from "@/Api/unities";
import { searchSellersData } from "@/Api/sellers";
import { getProductGroupsData } from "@/Api/productGroups";
import { toast } from "sonner";
import { TableData } from "./Table";
import { SearchableSelect } from "./components/SearchableSelect";



interface FilterData {
  contest_status_id?: number;
  unity_id?: number;
  contest_id?: number;
  lottery_id?: number;
  seller_id?: number;
  start_date?: Date;
  end_date?: Date;
  updated_from?: Date;
  updated_to?: Date;
  created_from?: Date;
  created_to?: Date;
  processed_from?: Date;
  processed_to?: Date;
  contest_validation_from?: Date;
  contest_validation_to?: Date;
  contest_name?: string;
  product_name?: string;
  product_classification_id?: number;
  updated_by?: number;
  created_by?: number;
  distribution_type_id?: number;
  status_id?: number;
  from_seller_id?: number;
  from_unity_id?: number;
  contest_product_classification_id?: number;
}

const processStatusOptions = [
  { value: "1", label: "Pendente", color: "bg-yellow-500" },
  { value: "2", label: "Aceito", color: "bg-green-500" },
  { value: "3", label: "Rejeitado", color: "bg-red-500" },
];

const processTypeOptions = [
  { value: "1", label: "Admin → Unidade" },
  { value: "2", label: "Admin → Vendedor" },
  { value: "3", label: "Unidade → Unidade" },
  { value: "4", label: "Vendedor → Vendedor" },
  { value: "5", label: "Vendedor → Unidade" },
  { value: "6", label: "Sobras" },
  { value: "7", label: "Pré-venda" },
];

const contestStatusOptions = [
  { value: "0", label: "Ativo" },
  { value: "1", label: "Encerrado" },
];

export function DistributionHistory() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState<FilterData>({});
  const [showFilters, setShowFilters] = useState(false);

  const [selectedContest, setSelectedContest] = useState<any>(null);
  const [selectedUnity, setSelectedUnity] = useState<any>(null);
  const [selectedSeller, setSellersSelected] = useState<any>(null);
  const [selectedLottery, setSelectedLottery] = useState<any>(null);
  const [selectedSenderSeller, setSelectedSenderSeller] = useState<any>(null);
  const [selectedReceiverSeller, setSelectedReceiverSeller] = useState<any>(null);
  const [selectedSenderUnity, setSelectedSenderUnity] = useState<any>(null);
  const [selectedReceiverUnity, setSelectedReceiverUnity] = useState<any>(null);

  const [lotteries, setLotteries] = useState<any[]>([]);

  const loadSelectData = useCallback(async () => {
    try {
      const lotteriesRes = await getProductGroupsData(1);
      if (lotteriesRes.data?.response?.data) {
        setLotteries(lotteriesRes.data.response.data);
      }
    } catch (error) {
      console.error("Error loading select data:", error);
    }
  }, []);

  const fetchData = useCallback(async (page: number = 1) => {
    setLoading(true);
    try {
      const params = {
        page,
        contest_status_id: filters.contest_status_id,
        unity_id: selectedReceiverUnity?.id || selectedUnity?.id,
        contest_id: selectedContest?.id,
        lottery_id: selectedLottery?.id,
        seller_id: selectedReceiverSeller?.id || selectedSeller?.id,
        start_date: filters.start_date ? format(filters.start_date, "yyyy-MM-dd") : undefined,
        end_date: filters.end_date ? format(filters.end_date, "yyyy-MM-dd") : undefined,
        updated_from: filters.updated_from ? format(filters.updated_from, "yyyy-MM-dd") : undefined,
        updated_to: filters.updated_to ? format(filters.updated_to, "yyyy-MM-dd") : undefined,
        created_from: filters.created_from ? format(filters.created_from, "yyyy-MM-dd") : undefined,
        created_to: filters.created_to ? format(filters.created_to, "yyyy-MM-dd") : undefined,
        processed_from: filters.processed_from ? format(filters.processed_from, "yyyy-MM-dd") : undefined,
        processed_to: filters.processed_to ? format(filters.processed_to, "yyyy-MM-dd") : undefined,
        contest_validation_from: filters.contest_validation_from ? format(filters.contest_validation_from, "yyyy-MM-dd") : undefined,
        contest_validation_to: filters.contest_validation_to ? format(filters.contest_validation_to, "yyyy-MM-dd") : undefined,
        contest_name: filters.contest_name,
        product_name: filters.product_name,
        product_classification_id: filters.product_classification_id,
        updated_by: filters.updated_by,
        created_by: filters.created_by,
        distribution_type_id: filters.distribution_type_id,
        status_id: filters.status_id,
        from_seller_id: selectedSenderSeller?.id,
        from_unity_id: selectedSenderUnity?.id,
        contest_product_classification_id: filters.contest_product_classification_id,
      };

      const {data} = await getDistributionProcessesAll(params);

      if (data?.response) {
        setData(data.response);
      }
    } catch (error) {
      toast.error("Erro ao carregar histórico de distribuições");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters, selectedContest?.id, selectedLottery?.id, selectedSeller?.id, selectedSenderSeller?.id, selectedReceiverSeller?.id, selectedSenderUnity?.id, selectedReceiverUnity?.id, selectedUnity?.id]);

  useEffect(() => {
    loadSelectData();
    fetchData();
  }, []);

  useEffect(() => {
    if (data !== null) {
      fetchData(1);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, selectedContest, selectedLottery, selectedSeller, selectedSenderSeller, selectedReceiverSeller, selectedSenderUnity, selectedReceiverUnity, selectedUnity]);

  const handleFilterChange = (key: keyof FilterData, value: any) => {
    setFilters(prev => ({
      ...prev,
      [key]: value === "all" ? undefined : value,
    }));
  };

  const handleApplyFilters = () => {
    fetchData(1);
    setShowFilters(false);
  };

  const handleClearFilters = () => {
    setFilters({});
    setSelectedContest(null);
    setSelectedUnity(null);
    setSellersSelected(null);
    setSelectedLottery(null);
    setSelectedSenderSeller(null);
    setSelectedReceiverSeller(null);
    setSelectedSenderUnity(null);
    setSelectedReceiverUnity(null);
    fetchData(1);
  };

  const handlePageChange = (page: number) => {
    if (page >= 1 && page <= (data?.last_page || 1)) {
      fetchData(page);
    }
  };

  const pageActions = {
    previous: () => handlePageChange((data?.current_page || 1) - 1),
    next: () => handlePageChange((data?.current_page || 1) + 1),
    lastPage: () => handlePageChange(data?.last_page || 1),
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="max-w-7xl mx-auto p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Histórico de Distribuições</h1>
            <p className="text-gray-600 mt-1">Acompanhe todas as movimentações de distribuição</p>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              size="icon"
              onClick={() => fetchData(data?.current_page || 1)}
              disabled={loading}
            >
              <RefreshCw className={cn("h-4 w-4", loading && "animate-spin")} />
            </Button>
            <Button
              variant={showFilters ? "default" : "outline"}
              onClick={() => setShowFilters(!showFilters)}
            >
              <Filter className="h-4 w-4 mr-2" />
              Filtros
              {(Object.keys(filters).filter(k => filters[k as keyof FilterData] !== undefined).length > 0 ||
                selectedContest || selectedUnity || selectedSeller || selectedLottery ||
                selectedSenderSeller || selectedReceiverSeller || selectedSenderUnity || selectedReceiverUnity) && (
                <Badge className="ml-2 bg-red-500 text-white">
                  {Object.keys(filters).filter(k => filters[k as keyof FilterData] !== undefined).length +
                   [selectedContest, selectedUnity, selectedSeller, selectedLottery,
                    selectedSenderSeller, selectedReceiverSeller, selectedSenderUnity, selectedReceiverUnity]
                   .filter(Boolean).length}
                </Badge>
              )}
            </Button>
          </div>
        </div>

        {(selectedContest || selectedUnity || selectedSeller || selectedLottery ||
          selectedSenderSeller || selectedReceiverSeller || selectedSenderUnity || selectedReceiverUnity ||
          Object.keys(filters).some(k => filters[k as keyof FilterData] !== undefined)) && (
          <Card className="mb-4">
            <CardContent className="pt-4">
              <div className="flex flex-wrap gap-2 items-center">
                <span className="text-sm font-medium text-gray-700">Filtros ativos:</span>
                {selectedContest && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Concurso: {selectedContest.contest_number || selectedContest.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedContest(null)} />
                  </Badge>
                )}
                {selectedLottery && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full" style={{ backgroundColor: selectedLottery.color }} />
                    {selectedLottery.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedLottery(null)} />
                  </Badge>
                )}
                {selectedUnity && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Unidade: {selectedUnity.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedUnity(null)} />
                  </Badge>
                )}
                {selectedSeller && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Vendedor: {selectedSeller.user?.name || selectedSeller.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSellersSelected(null)} />
                  </Badge>
                )}
                {selectedSenderSeller && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    De: {selectedSenderSeller.user?.name || selectedSenderSeller.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedSenderSeller(null)} />
                  </Badge>
                )}
                {selectedReceiverSeller && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Para: {selectedReceiverSeller.user?.name || selectedReceiverSeller.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedReceiverSeller(null)} />
                  </Badge>
                )}
                {selectedSenderUnity && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    De: {selectedSenderUnity.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedSenderUnity(null)} />
                  </Badge>
                )}
                {selectedReceiverUnity && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Para: {selectedReceiverUnity.name}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => setSelectedReceiverUnity(null)} />
                  </Badge>
                )}
                {filters.status_id && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Status: {processStatusOptions.find(o => o.value === filters.status_id?.toString())?.label}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange("status_id", undefined)} />
                  </Badge>
                )}
                {filters.distribution_type_id && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Tipo: {processTypeOptions.find(o => o.value === filters.distribution_type_id?.toString())?.label}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange("distribution_type_id", undefined)} />
                  </Badge>
                )}
                {filters.start_date && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    De: {format(filters.start_date, "dd/MM/yyyy")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange("start_date", undefined)} />
                  </Badge>
                )}
                {filters.end_date && (
                  <Badge variant="secondary" className="flex items-center gap-1">
                    Até: {format(filters.end_date, "dd/MM/yyyy")}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => handleFilterChange("end_date", undefined)} />
                  </Badge>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {showFilters && (
          <Card>
            <CardContent className="pt-6">
              <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-4">
                <div className="space-y-2">
                  <Label>Status do Concurso</Label>
                  <Select
                    value={filters.contest_status_id?.toString() || "all"}
                    onValueChange={(value) => handleFilterChange("contest_status_id", value === "all" ? undefined : Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {contestStatusOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Tipo de Distribuição</Label>
                  <Select
                    value={filters.distribution_type_id?.toString() || "all"}
                    onValueChange={(value) => handleFilterChange("distribution_type_id", value === "all" ? undefined : Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {processTypeOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Status do Processo</Label>
                  <Select
                    value={filters.status_id?.toString() || "all"}
                    onValueChange={(value) => handleFilterChange("status_id", value === "all" ? undefined : Number(value))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos</SelectItem>
                      {processStatusOptions.map(option => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Loteria</Label>
                  <Select
                    value={selectedLottery?.id?.toString() || "all"}
                    onValueChange={(value) => {
                      if (value === "all") {
                        setSelectedLottery(null);
                      } else {
                        const lottery = lotteries.find(l => l.id.toString() === value);
                        setSelectedLottery(lottery);
                      }
                    }}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas</SelectItem>
                      {lotteries.map(lottery => (
                        <SelectItem key={lottery.id} value={lottery.id.toString()}>
                          <div className="flex items-center gap-2">
                            <div
                              className="w-3 h-3 rounded-full border"
                              style={{ backgroundColor: lottery.color }}
                            />
                            {lottery.name}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label>Concurso</Label>
                  <SearchableSelect
                    placeholder="Buscar concurso"
                    searchFunction={(query) => searchContestsData({ name: query })}
                    onSelect={setSelectedContest}
                    value={selectedContest}
                    formatOption={(option) => `${option.contest_number || option.name} - ${option.lottery?.name || ''}`}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Unidade</Label>
                  <SearchableSelect
                    placeholder="Buscar unidade"
                    searchFunction={(query) => searchUnitiesData({ name: query })}
                    onSelect={setSelectedUnity}
                    value={selectedUnity}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Vendedor</Label>
                  <SearchableSelect
                    placeholder="Buscar vendedor"
                    searchFunction={(query) => searchSellersData({ name: query })}
                    onSelect={setSellersSelected}
                    value={selectedSeller}
                    formatOption={(option) => option.user?.name || option.name}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Data Inicial</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.start_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.start_date ? format(filters.start_date, "dd/MM/yyyy") : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.start_date}
                        onSelect={(date) => handleFilterChange("start_date", date)}
                        locale={ptBR}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Data Final</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.end_date && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.end_date ? format(filters.end_date, "dd/MM/yyyy") : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.end_date}
                        onSelect={(date) => handleFilterChange("end_date", date)}
                        locale={ptBR}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

{/*
                <div className="space-y-2">
                  <Label>Atualizado De</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.updated_from && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.updated_from ? format(filters.updated_from, "dd/MM/yyyy") : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.updated_from}
                        onSelect={(date) => handleFilterChange("updated_from", date)}
                        locale={ptBR}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>

                <div className="space-y-2">
                  <Label>Atualizado Até</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button
                        variant="outline"
                        className={cn(
                          "w-full justify-start text-left font-normal",
                          !filters.updated_to && "text-muted-foreground"
                        )}
                      >
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {filters.updated_to ? format(filters.updated_to, "dd/MM/yyyy") : "Selecione"}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0" align="start">
                      <Calendar
                        mode="single"
                        selected={filters.updated_to}
                        onSelect={(date) => handleFilterChange("updated_to", date)}
                        locale={ptBR}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                */}

                <div className="space-y-2">
                  <Label>Remetente (Vendedor)</Label>
                  <SearchableSelect
                    placeholder="Buscar vendedor remetente"
                    searchFunction={(query) => searchSellersData({ name: query })}
                    onSelect={setSelectedSenderSeller}
                    value={selectedSenderSeller}
                    formatOption={(option) => option.user?.name || option.name}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Destinatário (Vendedor)</Label>
                  <SearchableSelect
                    placeholder="Buscar vendedor destinatário"
                    searchFunction={(query) => searchSellersData({ name: query })}
                    onSelect={setSelectedReceiverSeller}
                    value={selectedReceiverSeller}
                    formatOption={(option) => option.user?.name || option.name}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Remetente (Unidade)</Label>
                  <SearchableSelect
                    placeholder="Buscar unidade remetente"
                    searchFunction={(query) => searchUnitiesData({ name: query })}
                    onSelect={setSelectedSenderUnity}
                    value={selectedSenderUnity}
                  />
                </div>

                <div className="space-y-2">
                  <Label>Destinatário (Unidade)</Label>
                  <SearchableSelect
                    placeholder="Buscar unidade destinatária"
                    searchFunction={(query) => searchUnitiesData({ name: query })}
                    onSelect={setSelectedReceiverUnity}
                    value={selectedReceiverUnity}
                  />
                </div>
              </div>

              <Separator className="my-4" />

              <div className="flex justify-end gap-2">
                <Button variant="outline" onClick={handleClearFilters}>
                  <X className="h-4 w-4 mr-2" />
                  Limpar
                </Button>
                <Button onClick={handleApplyFilters}>
                  <Filter className="h-4 w-4 mr-2" />
                  Aplicar Filtros
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {loading ? (
          <Card>
            <CardContent className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-gray-400" />
              <p className="text-gray-500 mt-2">Carregando...</p>
            </CardContent>
          </Card>
        ) : (
          data && <TableData dataApi={data} pageActions={pageActions} />
        )}
      </div>
    </div>
  );
}